import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import SecurityAIFAQ from "@/components/solutions/SecurityAIFAQ";

export default function AIGovernancePage() {
  return (
    <>
      <SEO
        title="AI Governance for Regulated Lab Documentation | LabComplyx"
        description="Control AI-generated quality work before it becomes audit risk. Compliance guardrails, human review checkpoints, and tamper-evident audit trails — see the full Governance & Trust FAQ for evidence-based answers."
        canonical="https://labcomplyx.com/solutions/ai-governance"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Solutions", url: "https://labcomplyx.com/solutions" },
          { name: "AI Governance", url: "https://labcomplyx.com/solutions/ai-governance" },
        ]}
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 max-w-2xl">
            <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">Solution</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5">Control AI-generated quality work before it becomes audit risk</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              LabComplyx wraps every AI output in compliance guardrails, human review checkpoints, and a tamper-evident audit trail — so AI becomes an accelerator, not an exposure.
            </p>
            <Link to="/ValidationIntake"><Button size="lg" className="gap-2">Learn How We Govern AI <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-5">Why uncontrolled AI is an audit risk</h2>
                <ul className="space-y-2.5">
                  {["AI hallucinations in compliance-critical records", "No traceability for how AI output was generated or reviewed", "Generic AI tools with no regulated-documentation guardrails", "FDA and CAP increasingly scrutinizing AI use in documentation", "Records that can't be defended if AI-assisted creation is questioned"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />{b}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">LabComplyx AI governance infrastructure</h3>
                <ul className="space-y-2.5">
                  {["Compliance guardrails injected into every AI prompt", "Blocking rules that reject non-compliant AI outputs before review", "Human-in-the-loop review checkpoint — required before record entry", "Full audit trail: AI model version, guardrails applied, reviewer sign-off", "Output provenance captured and exportable for inspector review"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />{b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="bg-card border border-border rounded-xl p-7">
              <p className="text-xs font-semibold text-secondary uppercase tracking-wide mb-3">Why the industry agrees</p>
              <blockquote className="text-base text-foreground leading-relaxed mb-3">
                AI-assisted work "is a powerful tool for quick development but should not be implicitly trusted" to produce secure, correct output on its own.
              </blockquote>
              <p className="text-sm text-muted-foreground leading-relaxed">
                That's the opening warning of Datadog's <em>State of DevSecOps 2026</em> study — from software security, not lab compliance, yet the lesson is identical: automation accelerates the work, but an accountable human checkpoint is what makes the speed defensible. It's the principle every LabComplyx workflow is built on.{" "}
                <Link to="/blog/devsecops-lessons-gxp-labs" className="text-primary underline">Read what GxP labs can learn from DevSecOps →</Link>
              </p>
            </div>
          </div>
        </section>

        <SecurityAIFAQ />
      </div>
    </>
  );
}